import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useApiClient } from "@/hooks/useApiClient";
import { type ICookiesType } from "@/types/cookies";
import { useUserMe } from "@/features/login/hooks/useUserMe";
import { useGeoSpaceApi } from "@/features/map/hooks/useGeospaceSearch";
import { saveMapServiceContractToLocalStorage } from "@/utils/mapServiceContract";
import { isIpNotAllowedState } from "@/utils/ipRestriction";
import { consumeIpNotAllowedReturnPath } from "@/utils/auth";

/** 許可されたネットワークへ戻ったかの確認に使うエンドポイント（許可IP外では403を返す） */
const TOKEN_VERIFY_ENDPOINT = "auth/jwt/verify";

/**
 * 許可IP外アクセス時の専用エラーページ
 *
 * IP制限は認証情報の問題ではないため、ログイン状態は保持したまま本画面を表示する。
 * 許可されたネットワークへ切り替えれば、再ログイン無しで操作を再開できる。
 */
export const IpNotAllowed: React.FC = () => {
  const navigate = useNavigate();
  const [cookies] = useCookies();
  const { mypageTop } = useFeatureFlags();
  const { apiClient } = useApiClient();
  const { userMe } = useUserMe();
  const { geospaceGetMapServiceContract } = useGeoSpaceApi();
  const [isChecking, setIsChecking] = useState(false);

  // ログイン画面で拒否された場合など、セッションを持たずに本画面へ来ることがある。
  // アプリ画面の認証チェックは初回マウント時のみ動き画面内遷移では効かないため、
  // ログイン状態に応じて遷移先を切り替える
  const typedCookies = cookies as ICookiesType;
  const isLoggedIn = typedCookies.isLogin === "true";
  // 拒否された画面が記録されていない場合の既定の戻り先
  const fallbackPath = mypageTop ? "/mypage" : "/feed";

  /**
   * 許可されたネットワークへ戻ったかを確認し、戻っていれば操作を再開する
   *
   * トークン検証は許可IP外では403になるため、疎通確認として使える。
   * 403のときはインターセプタが本画面に留めるので、ここでは案内のみ行う。
   */
  const resumeIfAllowed = async (): Promise<void> => {
    setIsChecking(true);
    try {
      await apiClient.post(TOKEN_VERIFY_ENDPOINT, {
        token: typedCookies.accesstoken,
      });

      // 許可IP外でログインした場合、ログイン直後のユーザー情報と地図サービス契約の
      // 取得は403で失敗している。どちらもアプリ画面のマウント時には再取得されないため、
      // ここで取り直さないと氏名が空・地図が契約なしのまま操作を再開してしまう
      userMe();
      const mapServiceContract = await geospaceGetMapServiceContract();

      // 取り直しの途中で再び拒否された場合、取得失敗を「契約なし」として保存すると
      // 既存の契約情報を消してしまう。遷移もさせず本画面に留める
      if (isIpNotAllowedState()) {
        toast.error("まだ許可されたネットワークからアクセスできていません");
        return;
      }

      saveMapServiceContractToLocalStorage(mapServiceContract ?? null);

      // 拒否された画面へ戻す。記録が無ければ既定の画面へ
      navigate(consumeIpNotAllowedReturnPath() ?? fallbackPath, {
        replace: true,
      });
    } catch {
      toast.error("まだ許可されたネットワークからアクセスできていません");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f4f4f4",
        textAlign: "center",
        padding: 2,
      }}
    >
      <Typography variant="h2" sx={{ marginBottom: 2, color: "#333" }}>
        🔒
      </Typography>
      <Typography variant="h2" sx={{ marginBottom: 6, color: "#333" }}>
        このネットワークからはご利用いただけません
      </Typography>
      <Typography variant="h6" sx={{ marginBottom: 4, color: "#666" }}>
        ご契約企業で許可されたネットワーク以外からはアクセスできません。
        <br />
        許可されたネットワークに接続すると、そのままご利用を再開できます。
        <br />
        ご不明な点は、貴社のシステム管理者へお問い合わせください。
      </Typography>
      {isLoggedIn ? (
        <Button
          variant="contained"
          disabled={isChecking}
          onClick={() => {
            void resumeIfAllowed();
          }}
        >
          操作を再開する
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={() => {
            navigate("/login", { replace: true });
          }}
        >
          ログイン画面へ
        </Button>
      )}
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="body2" color="textSecondary">
          &copy; {new Date().getFullYear()} TRUSTART, Inc. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};
